/* eslint-disable max-len */
import {Flex, Button} from 'antd';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {ReactComponent as YellowUpgradeCrown} from '../../assets/icons/YellowUpgradeCrown.svg';
import TableBg from '../../assets/skeleton/TableBg.png';
import {
  blockAccessTypes,
  blockModelTypes
} from '../../constants/blockPageConstatnts';
import {Routes} from '../../constants/Routes';

// Shared styles
const overlayStyle = {
  top: 0,
  width: '100%',
  height: '100%',
  padding: '60px 150px',
  minHeight: '600px'
};

const bannerStyle = {
  backgroundColor: '#E9F5E5',
  borderRadius: 8,
  padding: '16px 24px',
  width: '100%'
};

const crownWrapperStyle = {
  backgroundColor: '#fff',
  borderRadius: '50%',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

// Reusable crown icon component
const CrownIcon: React.FC<{size?: 'small' | 'large'}> = ({size = 'large'}) => (
  <Flex align="center" justify="center" className="upgradeCrownContainer">
    {size === 'small' ? (
      <div style={crownWrapperStyle}>
        <YellowUpgradeCrown />
      </div>
    ) : (
      <YellowUpgradeCrown />
    )}
  </Flex>
);

interface BlockAccessPageProps {
  blockedType?: blockAccessTypes;
  modelType?: blockModelTypes;
  messageTitle?: string;
  messageDescription?: string;
  style?: React.CSSProperties;
  backgroundImage?: string;
}

const BlockAccessPage: React.FC<BlockAccessPageProps> = ({
  blockedType = blockAccessTypes.FULL,

  modelType = '',
  messageTitle = 'Upgrade to Access Full Features & Insights',
  messageDescription = `
        You’ve reached a premium feature that’s part of our advanced tools.
        <br/>
        Unlock full access to detailed analytics, AI support, and reporting
        capabilities by upgrading your current plan.
      `,
  style = overlayStyle,
  backgroundImage = TableBg // Default to Table
}) => {
  const navigate = useNavigate();

  const handleUpgrade = (): void => {
    navigate(Routes.PRICING_DETAILS);
  };

  let messageDescriptionUpdated;
  if (messageDescription?.includes('<br/>')) {
    messageDescriptionUpdated = (
      <>
        {messageDescription?.split('<br/>')?.[0]}
        <br />
        {messageDescription?.split('<br/>')?.[1]}
      </>
    );
  } else {
    messageDescriptionUpdated = messageDescription;
  }

  if (modelType === blockModelTypes.SCOPE_TOP) {
    return (
      <Flex align="center" justify="space-between" style={bannerStyle}>
        <Flex align="center" gap={16}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 50,
              height: 50,
              border: '1px solid #faad14',
              borderRadius: '50%'
            }}
          >
            <CrownIcon size="large" />
          </div>
          <Flex vertical>
            <div style={{fontWeight: 600, fontSize: 20, color: '#0D1C14'}}>
              {messageTitle}
            </div>
            <div style={{color: '#4B5563', fontSize: 14, paddingTop: 8}}>
              {messageDescriptionUpdated}
            </div>
          </Flex>
        </Flex>
        <Button
          type="primary"
          style={{
            backgroundColor: '#334B35',
            borderColor: '#334B35',
            height: 36,
            paddingInline: 20,
            borderRadius: 6
          }}
          onClick={handleUpgrade}
        >
          Upgrade Now
        </Button>
      </Flex>
    );
  }

  // Full/Partial overlay for other types
  return (
    <div
      style={
        blockedType === blockAccessTypes.FULL
          ? {
              minHeight: '100vh',
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...style
            }
          : {}
      }
    >
      <Flex
        className="pageAccessBlockContainer"
        vertical
        align="center"
        justify="center"
      >
        <CrownIcon />

        <Flex vertical gap={10} style={{textAlign: 'center', maxWidth: 600}}>
          <div className="descriptionTitle">{messageTitle}</div>
          <div className="descriptionData">{messageDescriptionUpdated}</div>
        </Flex>

        <Button type="primary" style={{height: '40px'}} onClick={handleUpgrade}>
          Upgrade Now
        </Button>
      </Flex>
    </div>
  );
};

export default BlockAccessPage;
